import { navigateToUrl } from 'single-spa'
import { getCookie, removeCookie, setCookie } from 'typescript-cookie'
import {
  AUTH_TOKEN_KEY,
  LocalStorage,
  LocalStorageAuthKey,
  UserType,
} from './constants'
import client from './hasuraClient'
import { dispatchLocalStorage, removeLocalStorage } from './localStorage'
import { Product } from './products'

const getSession = async (): Promise<string | null> => {
  const sessionRequest = new Request(
    `${process.env.AUTH_APP_URL}/api/session`,
    {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ 'Content-Type': 'application/json' }),
    }
  )

  const sessionResponse = await fetch(sessionRequest)
  if (sessionResponse.status !== 200)
    throw new Error(sessionResponse.statusText)

  const { user, accessToken } = await sessionResponse.json()
  localStorage.setItem(LocalStorage.EMAIL, user.email)
  if (!getCookie(AUTH_TOKEN_KEY))
    setCookie(AUTH_TOKEN_KEY, accessToken, { secure: true, path: '/' })

  return accessToken
}

const getUserInfo = async (accessToken: string): Promise<string> => {
  const userInfoRequest = new Request(
    `${process.env.API_KRAKEND}/auth/userInfoToken`,
    {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      }),
    }
  )

  const userInfoResponse = await fetch(userInfoRequest)
  if (userInfoResponse.status !== 200)
    throw new Error(userInfoResponse.statusText)

  const userInfo = await userInfoResponse.json()
  const id = userInfo['user_root']?.id || userInfo.id
  const fullName = `${userInfo.first_name} ${userInfo.last_name}`

  localStorage.setItem(LocalStorage.USER_ID, id)
  localStorage.setItem(LocalStorage.USER_NAME, `${fullName}`)
  if (id !== userInfo.id)
    localStorage.setItem(LocalStorage.USER_CHILD_ID, userInfo.id)

  return id
}

export const getCustomerStatus = async (
  customerId: string
): Promise<Array<string>> => {
  const body = {
    query: `
      query getCustomerStatus($query: JSON = "") {
        getCustomerStatus(query: $query) {
          data
          error {
            data
            message
            name
          }
          success
        }
      }
    `,
    variables: {
      query: {
        customerId,
      },
    },
  }

  const data = await client(body)

  if (!data?.getCustomerStatus?.data) {
    throw new Error('Invalid customer info')
  }

  const {
    companyName,
    businessId,
    taxId,
    productsData,
    customerType,
    agentName,
    pendingABPCount,
  } = data.getCustomerStatus.data

  localStorage.setItem(LocalStorage.COMPANY_NAME, companyName || '')
  localStorage.setItem(LocalStorage.BUSINESS_ID, businessId || '')
  localStorage.setItem(LocalStorage.TAX_ID, taxId)

  let products = {}
  if (productsData.length > 0) {
    localStorage.setItem(
      LocalStorage.ACTIVATED_PRODUCTS,
      Array.from(new Set(productsData)).toString()
    )
    products = productsData.reduce(
      (prev: any, product: string) => ({
        ...prev,
        [product.toUpperCase()]: true,
      }),
      {}
    )
  }
  localStorage.setItem(
    LocalStorage.IN_ONBOARDING,
    `${!products[Product.invoiceAdvance.toUpperCase()] && pendingABPCount > 0}`
  )

  dispatchLocalStorage({
    key: LocalStorage.CUSTOMER_TYPE,
    value: customerType,
  })

  if (customerType === UserType.FFW_CUSTOMER) {
    if (!!agentName) {
      dispatchLocalStorage({
        key: LocalStorage.FFW_NAME,
        value: agentName,
      })
    }
  } else {
    removeLocalStorage({ key: LocalStorage.FFW_NAME })
  }

  return Array.from(new Set(Object.keys(products)))
}

const LOGIN_URL_WITH_REDIRECT = `${process.env.AUTH_APP_URL}/api/logout?redirectUri=${window.location.origin}/`

const logout = () => {
  LocalStorageAuthKey.forEach((item) => localStorage.removeItem(item))
  removeCookie(AUTH_TOKEN_KEY)
  navigateToUrl(LOGIN_URL_WITH_REDIRECT)
}

addEventListener('logout', () => {
  logout()
})

export { getSession, getUserInfo, logout }
